// Wrap-up outcomes on keys 1–6. Every value is a real `calls.disposition`
// enum member (lib/nexus_dialer/schemas/call.ex). The set follows what
// happened: a conversation gets conversation outcomes; a call nobody answered
// gets the no-contact outcomes. Callback asks for a date; do-not-call is the
// only destructive one.

export const CONNECTED_OUTCOMES = [
  { value: "interested", label: "Interested", hint: "Wants a next step" },
  { value: "callback", label: "Callback", hint: "Pick a date and time", followUp: "call" },
  { value: "not_interested", label: "Not interested", hint: "Closes this lead" },
  { value: "voicemail", label: "Voicemail", hint: "Left a message" },
  { value: "wrong_number", label: "Wrong number", hint: "Flags the number" },
  { value: "do_not_call", label: "Do not call", hint: "Stops all calling", tone: "danger" },
];

export const UNANSWERED_OUTCOMES = [
  { value: "no_answer", label: "No answer", hint: "Retried later" },
  { value: "voicemail", label: "Voicemail", hint: "Left a message" },
  { value: "busy", label: "Busy", hint: "Retried later" },
  { value: "callback", label: "Callback", hint: "Pick a date and time", followUp: "call" },
  { value: "wrong_number", label: "Wrong number", hint: "Flags the number" },
  { value: "do_not_call", label: "Do not call", hint: "Stops all calling", tone: "danger" },
];

export const outcomesFor = (connected) => (connected ? CONNECTED_OUTCOMES : UNANSWERED_OUTCOMES);

/* Follow-up actions accepted by variables.workflow.followUpAction. */
export const FOLLOW_UP_ACTIONS = [
  { value: "call", label: "Call back" },
  { value: "email", label: "Send email" },
  { value: "meeting", label: "Book meeting" },
  { value: "quote", label: "Send quote" },
];

export const outcomeLabel = (value) =>
  [...CONNECTED_OUTCOMES, ...UNANSWERED_OUTCOMES].find((o) => o.value === value)?.label ||
  (value ? String(value).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "");

/**
 * Validates a wrap-up draft. Disposition is required; a follow-up action needs
 * a date and time, and a date needs an action. Returns { field: message }.
 */
export const validateWrapUp = (draft) => {
  const errors = {};
  if (!draft.outcome) errors.outcome = "Choose how the call went (1–6).";
  if (draft.followUpAction && !draft.followUpAt)
    errors.followUpAt = "Pick a date and time for the follow-up.";
  if (draft.followUpAt && !draft.followUpAction)
    errors.followUpAction = "Choose what the follow-up is.";
  return errors;
};

/**
 * The single atomic save (flow-rail-workflow.md): notes, disposition, status
 * and follow-up go through the call's disposition endpoint together.
 * `clearFollowUp` sends an explicit null, which clears a stored date; an
 * omitted follow-up leaves the contact's date unchanged.
 */
export const dispositionPayload = (draft, { contactId, campaignId }) => {
  // Do-not-call is persisted by the disposition itself (Calls.persist_dnc_*),
  // so the session never sends a separate contact status.
  const workflow = { contactId, campaignId, contactStatus: null };
  if (draft.followUpAction && draft.followUpAt) {
    workflow.followUpAction = draft.followUpAction;
    workflow.followUpAt = draft.followUpAt;
  } else if (draft.clearFollowUp) {
    workflow.followUpAction = null;
    workflow.followUpAt = null;
  }
  return {
    outcome: draft.outcome,
    notes: draft.notes || "",
    variables: { workflow },
  };
};
